import axios from '~/utils/axiosInstance';
import { storage } from '~/utils';

export const signup = async function (data: any = {}) {
  try {
    const response = await axios.post('/user/signup', {
      name: data.name,
      email: data.email,
      password: data.password,
      companyName: data.company,
      companyTags: data.companyTags,
      roleId: data.roleId,
      inviteToken: data.inviteToken,
      recaptchaToken: data.recaptchaToken
    });

    // Signup endpoint automatically logs the user in.
    //  Store the user data in local storage
    storage.storeCurrentUser(response.data);
    
    return response.data;
  } catch (e: any) {
    if (e?.request?.response && typeof e.request.response === 'string') {
      throw new Error(JSON.parse(e?.request?.response)?.message);
    }
    throw e;
  }
};

export const fetchRoles = async function () {
  const response = await axios.get('/user/roles');
  return response.data;
};

export const fetchNotifications = async function (params: any = {}) {
  const response = await axios.get('/user/notifications', {params});
  return {
    ...response.data,
    nextPage: (params?.page ?? 0) + 1
  };
};

export const fetchUnreadNotificationsCount = async function () {
  const response = await axios.get('/user/notifications/total-unread');
  return response.data;
};

export const fetchLoggedInUser = async function () {
  // try {
  //   const localStorageUser = storage.getStoredCurrentUser();
  //   if (localStorageUser) {
  //     return localStorageUser;
  //   }
  // } catch (e: any) {
  //   // ignored
  // }
  const user = await axios.get('/user').then(response => response.data);
  storage.storeCurrentUser(user);
  return user;
};

export const updateUserSettings = async function (data: any = {}) {
  const user = await axios.patch('/user', data).then(response => response.data);
  const currentUser = storage.getStoredCurrentUser();
  storage.storeCurrentUser({...currentUser, ...user});
  return user;
};

export const updateUserPassword = async function (data: any = {}) {
  await axios.post('/user/password', data).then(response => response.data);
};

export type NotificationPreferencesObject = {
  code: string;
  name: string;
  externalUserExempt?: boolean;
  selections: {
    code: 'LEAD' | 'MEMBER' | 'NON_MEMBER';
    selected: boolean;
    updateable: boolean;
  }[]
}[];
export type NotificationPreferencesResponse = {
  developer: NotificationPreferencesObject;
  investor: NotificationPreferencesObject;
};
export const fetchNotificationPreferences = async function () {
  const response = await axios.get('/user/notification-preferences');
  return response.data as NotificationPreferencesResponse;
};

export type UpdateNotificationPreferences = {
  action: 'add' | 'delete';
  group: 'developer' | 'investor';
  preferences: { code: string; category: string } | { code: string; category: string }[];
};
export const updateNotificationPreferences = async function (data: UpdateNotificationPreferences) {
  const response = await axios.put('/user/notification-preferences', data);
  return response.data as NotificationPreferencesResponse;
};

export const updateCompanyRole = async function (userId: string, inviteId: string, companyRole: string) {
  const response = await axios.put('/user/company-role', userId ? {userId, companyRole} : {inviteId, companyRole});
  return response.data;
};

export const getPermissions = async function () {
  const response = await axios.get('/user/permissions');
  return response.data;
};

export const deactivateUser = async function (userId: string) {
  const response = await axios.delete(`user/${userId}`);
  return response.data;
};

export const UserActivityTypes = {
  PROJECT_VIEW: 'PROJECT_VIEW',
  PORTFOLIO_VIEW: 'PORTFOLIO_VIEW',
  DATAROOM_DOCUMENT_DOWNLOAD: 'DATAROOM_DOCUMENT_DOWNLOAD',
  DATAROOM_BUNDLE_DOWNLOAD: 'DATAROOM_BUNDLE_DOWNLOAD',
  MESSAGE_SENT: 'MESSAGE_SENT',
  SUMMARY_DOCUMENT_DOWNLOAD: 'SUMMARY_DOCUMENT_DOWNLOAD',
  DEAL_SPREADSHEET_DOWNLOAD: 'DEAL_SPREADSHEET_DOWNLOAD',
  BID_SUBMITTED: 'BID_SUBMITTED',
  BID_ACCEPTED: 'BID_ACCEPTED',
  DECLINED_TO_BID: 'DECLINED_TO_BID',
  INVESTOR_REJECTED: 'INVESTOR_REJECTED',
  LISTING_INTEREST: 'LISTING_INTEREST',
} as const;

export type LogUserActivityParams = {
  activityType: keyof typeof UserActivityTypes | string;
  projectId?: string | null;
  portfolioId?: string | null;
  metadata?: Record<string, unknown> | null;
};

/**
 * Record user activity. Fire-and-forget: does not await or throw to the caller.
 * Call in parallel to the main action (e.g. after requesting a download URL).
 */
export const logUserActivity = function (params: LogUserActivityParams) {
  const { activityType, projectId, portfolioId, metadata } = params;
  axios
    .post('/user-activity', {
      activityType,
      projectId: projectId ?? null,
      portfolioId: portfolioId ?? null,
      metadata: metadata ?? null,
    })
    .catch(() => {
      // Intentionally fire-and-forget; do not surface errors to the user.
    });
};